(() => {
  if (window.MoztechScrollGallery) {
    window.MoztechScrollGallery.init(document);
    return;
  }

  const selector = '[data-moz-scroll-gallery]';
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const activeViewportMargin = 0.25;
  const imageWarmScrollQuietMs = 180;
  const nativeScrollMotion = window.CSS?.supports?.('animation-timeline: view()')
    && window.CSS?.supports?.('animation-range: cover 0% cover 100%')
    && window.CSS?.supports?.('animation-duration: auto')
    && window.CSS?.supports?.('view-timeline-name: --moz-gallery-scroll');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const quantize = (value, step) => Math.round(value / step) * step;

  const destroy = (sectionId) => {
    const instance = instances.get(sectionId);
    if (!instance) return;

    window.removeEventListener('scroll', instance.onScroll);
    window.removeEventListener('resize', instance.onResize);
    document.removeEventListener('visibilitychange', instance.onVisibilityChange);
    instance.root.removeEventListener('pointermove', instance.onPointerMove);
    instance.root.removeEventListener('pointerleave', instance.onPointerLeave);
    instance.observer?.disconnect();
    instance.imageWarmObserver?.disconnect();
    if (instance.scrollIdleHandle) window.clearTimeout(instance.scrollIdleHandle);
    if (instance.imageWarmHandle) {
      if (instance.imageWarmUsesIdle && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(instance.imageWarmHandle);
      } else {
        window.clearTimeout(instance.imageWarmHandle);
      }
    }
    instance.imageWarmToken += 1;
    if (instance.frame) window.cancelAnimationFrame(instance.frame);
    instance.styleValues.forEach((properties, element) => {
      properties.forEach((_value, property) => element.style.removeProperty(property));
    });
    instance.root.classList.remove('is-motion-ready', 'is-page-scrolling');
    instances.delete(sectionId);
  };

  const create = (root) => {
    if (!root) return;

    const sectionId = root.dataset.sectionId;
    destroy(sectionId);

    const textEffectsEnabled = root.dataset.textInteraction !== 'false';
    const pointerEffectsEnabled = textEffectsEnabled && finePointer.matches;
    const columns = Array.from(root.querySelectorAll('.moz-scroll-gallery__column'));
    const copyInner = root.querySelector('.moz-scroll-gallery__copy-inner');
    const copyContent = root.querySelector('.moz-scroll-gallery__copy-content');
    const eyebrowText = root.querySelector('.moz-scroll-gallery__eyebrow-text');
    const signalLine = root.querySelector('.moz-scroll-gallery__signal-line');
    const heading = root.querySelector('.moz-scroll-gallery__heading');
    const headingText = root.querySelector('.moz-scroll-gallery__heading-text');
    const state = {
      root,
      active: false,
      frame: 0,
      observer: null,
      imageWarmObserver: null,
      imageWarmHandle: 0,
      imageWarmUsesIdle: false,
      imageWarmToken: 0,
      scrollIdleHandle: 0,
      lastScrollAt: 0,
      rect: null,
      pointerX: 0,
      pointerY: 0,
      motionReset: false,
      styleValues: new Map(),
      onScroll: null,
      onResize: null,
      onVisibilityChange: null,
      onPointerMove: null,
      onPointerLeave: null
    };

    const setStyle = (element, property, value) => {
      if (!element) return;
      let properties = state.styleValues.get(element);
      if (!properties) {
        properties = new Map();
        state.styleValues.set(element, properties);
      }
      if (properties.get(property) === value) return;
      properties.set(property, value);
      element.style.setProperty(property, value);
    };

    const updateColumnMotion = (fallbackShift) => {
      if (nativeScrollMotion) return;

      columns.forEach((column, index) => {
        const direction = index % 2 === 0 ? 1 : -1;
        setStyle(column, 'transform', `translate3d(0, ${(fallbackShift * direction).toFixed(2)}px, 0)`);
      });
    };

    const cancelImageWarmHandle = () => {
      if (!state.imageWarmHandle) return;
      if (state.imageWarmUsesIdle && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(state.imageWarmHandle);
      } else {
        window.clearTimeout(state.imageWarmHandle);
      }
      state.imageWarmHandle = 0;
      state.imageWarmUsesIdle = false;
    };

    const scheduleImageWarmBatch = (callback) => {
      cancelImageWarmHandle();
      const runWhenSettled = () => {
        state.imageWarmHandle = 0;
        state.imageWarmUsesIdle = false;

        const quietFor = performance.now() - state.lastScrollAt;
        if (quietFor < imageWarmScrollQuietMs) {
          state.imageWarmHandle = window.setTimeout(
            runWhenSettled,
            Math.ceil(imageWarmScrollQuietMs - quietFor) + 24
          );
          return;
        }

        callback();
      };

      if ('requestIdleCallback' in window) {
        state.imageWarmUsesIdle = true;
        state.imageWarmHandle = window.requestIdleCallback(runWhenSettled, { timeout: 1500 });
      } else {
        state.imageWarmUsesIdle = false;
        state.imageWarmHandle = window.setTimeout(runWhenSettled, 72);
      }
    };

    const warmImages = () => {
      const token = ++state.imageWarmToken;
      let images = Array.from(root.querySelectorAll('.moz-scroll-gallery__image'))
        .filter((image) => image.tagName === 'IMG' && image.dataset.mozWarmReady !== 'true');
      if (window.innerWidth <= 989) {
        images = columns
          .map((column) => column.querySelector('.moz-scroll-gallery__image'))
          .filter((image) => image?.tagName === 'IMG' && image.dataset.mozWarmReady !== 'true');
      }
      let index = 0;

      const warmNextBatch = () => {
        if (token !== state.imageWarmToken || index >= images.length) return;
        const batch = images.slice(index, index + 2);
        index += batch.length;

        const decodes = batch.map((image) => {
          image.dataset.mozWarmReady = 'true';
          image.loading = 'eager';
          image.decoding = 'async';
          return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
        });

        Promise.allSettled(decodes).then(() => {
          if (token === state.imageWarmToken && index < images.length) {
            scheduleImageWarmBatch(warmNextBatch);
          }
        });
      };

      if (images.length) scheduleImageWarmBatch(warmNextBatch);
    };

    const resetMotion = () => {
      columns.forEach((column) => setStyle(column, 'transform', 'translate3d(0, 0px, 0)'));
      setStyle(copyInner, 'opacity', '1');
      setStyle(copyInner, 'transform', 'none');
      setStyle(copyContent, 'transform', 'none');
      setStyle(eyebrowText, 'background-position', '50% center');
      setStyle(headingText, 'background-position', '50% center');
      setStyle(signalLine, 'opacity', '1');
      setStyle(signalLine, 'transform', 'scaleX(1)');
      setStyle(heading, '--moz-gallery-text-parallax-x', '0px');
      setStyle(heading, '--moz-gallery-text-parallax-y', '0px');
      setStyle(heading, '--moz-gallery-outline-opacity', '0');
    };

    const update = () => {
      state.frame = 0;

      if (reducedMotion.matches) {
        if (!state.motionReset) resetMotion();
        state.motionReset = true;
        return;
      }
      state.motionReset = false;

      const rect = root.getBoundingClientRect();
      state.rect = rect;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height), 0, 1);
      const motionStrength = Number.parseFloat(root.dataset.motionStrength || '180');
      const shift = (progress - 0.5) * motionStrength * 2;
      const reveal = clamp(1 - Math.abs(progress - 0.5) * 2.15, 0, 1);
      const copyY = (0.5 - progress) * 52;
      const mobileShift = shift * 0.34;

      const textScale = textEffectsEnabled ? 0.95 + reveal * 0.05 : 1;
      const textShine = textEffectsEnabled ? 15 + progress * 70 : 50;
      const outlineOpacity = textEffectsEnabled ? reveal * 0.28 : 0;
      const tiltX = pointerEffectsEnabled ? state.pointerY * -2.4 : 0;
      const tiltY = pointerEffectsEnabled ? state.pointerX * 2.8 : 0;
      const parallaxX = pointerEffectsEnabled ? state.pointerX * -7 : 0;
      const parallaxY = pointerEffectsEnabled ? state.pointerY * -5 : 0;

      const mobileLayout = window.innerWidth <= 989;
      const columnShift = mobileLayout ? mobileShift : shift;
      updateColumnMotion(columnShift);

      const visualReveal = quantize(reveal, 0.025);
      const shinePosition = `${quantize(textShine, 4).toFixed(0)}% center`;
      setStyle(eyebrowText, 'background-position', shinePosition);
      setStyle(headingText, 'background-position', shinePosition);
      setStyle(heading, '--moz-gallery-text-parallax-x', `${parallaxX.toFixed(2)}px`);
      setStyle(heading, '--moz-gallery-text-parallax-y', `${parallaxY.toFixed(2)}px`);

      if (!nativeScrollMotion) {
        setStyle(signalLine, 'opacity', visualReveal.toFixed(3));
        setStyle(signalLine, 'transform', `scaleX(${visualReveal.toFixed(3)})`);
        setStyle(heading, '--moz-gallery-outline-opacity', quantize(outlineOpacity, 0.02).toFixed(2));
      }

      if (mobileLayout) {
        if (!nativeScrollMotion) {
          setStyle(copyInner, 'opacity', '1');
          setStyle(copyInner, 'transform', 'none');
        }
        setStyle(copyContent, 'transform', 'none');
      } else {
        if (!nativeScrollMotion) {
          setStyle(copyInner, 'opacity', reveal.toFixed(3));
          setStyle(copyInner, 'transform', `translate3d(0, ${copyY.toFixed(2)}px, 0) scale(${textScale.toFixed(4)})`);
        }
        setStyle(
          copyContent,
          'transform',
          `perspective(900px) rotateX(${tiltX.toFixed(2)}deg) rotateY(${tiltY.toFixed(2)}deg)`
        );
      }
    };

    const schedule = () => {
      if (!state.active || state.frame) return;
      state.frame = window.requestAnimationFrame(update);
    };

    state.onScroll = () => {
      state.lastScrollAt = performance.now();
      root.classList.add('is-page-scrolling');
      if (state.scrollIdleHandle) window.clearTimeout(state.scrollIdleHandle);
      state.scrollIdleHandle = window.setTimeout(() => {
        state.scrollIdleHandle = 0;
        root.classList.remove('is-page-scrolling');
      }, 140);
      schedule();
    };
    state.onResize = () => {
      state.rect = null;
      schedule();
    };
    state.onPointerMove = (event) => {
      if (!state.active || !pointerEffectsEnabled) return;

      const rect = state.rect;
      if (!rect?.width || !rect?.height) return;

      state.pointerX = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
      state.pointerY = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1, -1, 1);
      schedule();
    };
    state.onPointerLeave = () => {
      state.pointerX = 0;
      state.pointerY = 0;
      schedule();
    };
    state.onVisibilityChange = () => {
      if (document.hidden) {
        state.active = false;
        root.classList.remove('is-motion-ready');
        if (state.frame) {
          window.cancelAnimationFrame(state.frame);
          state.frame = 0;
        }
        return;
      }

      const rect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      state.active = rect.bottom > -viewportHeight * activeViewportMargin
        && rect.top < viewportHeight * (1 + activeViewportMargin);
      root.classList.toggle('is-motion-ready', state.active);
      if (state.active) schedule();
    };

    window.addEventListener('scroll', state.onScroll, { passive: true });
    window.addEventListener('resize', state.onResize, { passive: true });
    document.addEventListener('visibilitychange', state.onVisibilityChange);

    if (pointerEffectsEnabled) {
      root.addEventListener('pointermove', state.onPointerMove, { passive: true });
      root.addEventListener('pointerleave', state.onPointerLeave, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(
        (entries) => {
          state.active = entries.some((entry) => entry.isIntersecting);
          root.classList.toggle('is-motion-ready', state.active);
          if (state.active) {
            schedule();
          } else if (state.frame) {
            window.cancelAnimationFrame(state.frame);
            state.frame = 0;
          }
        },
        { rootMargin: '25% 0px' }
      );
      state.observer.observe(root);

      state.imageWarmObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        warmImages();
        state.imageWarmObserver?.disconnect();
        state.imageWarmObserver = null;
      }, { rootMargin: window.innerWidth <= 989 ? '45% 0px' : '110% 0px', threshold: 0 });
      state.imageWarmObserver.observe(root);
    } else {
      state.active = true;
      root.classList.add('is-motion-ready');
      warmImages();
    }

    const initialRect = root.getBoundingClientRect();
    const initialViewportHeight = window.innerHeight || document.documentElement.clientHeight;
    state.rect = initialRect;
    state.active = initialRect.bottom > -initialViewportHeight * activeViewportMargin
      && initialRect.top < initialViewportHeight * (1 + activeViewportMargin);

    if (state.active) {
      root.classList.add('is-motion-ready');
      schedule();
    }

    instances.set(sectionId, state);
  };

  const init = (scope = document) => {
    if (scope.matches?.(selector)) create(scope);
    scope.querySelectorAll?.(selector).forEach(create);
  };

  const api = { init, destroy, version: '2.3.0' };
  window.MoztechScrollGallery = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.detail.sectionId));
})();
